<template>
  <Teleport to="body">
  <Transition name="payment-term-modal-overlay">
    <div
      v-if="visible"
      class="payment-term-modal-overlay payment-term-modal-overlay--glass"
      @click.self="$emit('cancel')"
    >
      <Transition name="payment-term-modal-panel" appear>
        <div
          v-if="visible"
          class="payment-term-modal-card payment-term-modal-card--glass"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payment-term-modal-title"
          @click.stop
        >
          <div class="payment-term-modal-header payment-term-modal-header--glass">
            <h2 id="payment-term-modal-title" class="payment-term-modal-title">Add payment term</h2>
            <button
              type="button"
              class="payment-term-modal-close-btn"
              aria-label="Close"
              title="Close"
              @click="$emit('cancel')"
            >
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </div>

          <div class="payment-term-modal-body payment-term-modal-body--glass">
            <LabelInputField
              input-id="payment-term-name"
              label="Payment term"
              v-model="termName"
              placeholder="e.g. Net 30"
              required
              flex
              :disabled="saving"
            />

            <div class="payment-term-modal-type-section">
              <span class="payment-term-modal-type-label">Term type</span>
              <div class="payment-term-modal-radio-group">
                <label class="payment-term-modal-radio-label">
                  <input type="radio" value="fixed" v-model="termType" :disabled="saving" />
                  <span>Fixed Number of Days</span>
                </label>
                <label class="payment-term-modal-radio-label">
                  <input type="radio" value="date" v-model="termType" :disabled="saving" />
                  <span>Date Driven</span>
                </label>
              </div>
            </div>

            <div v-if="termType === 'fixed'" class="payment-term-modal-fields">
              <LabelInputField
                input-id="payment-term-net-due-in"
                label="Net due in"
                v-model="netDueIn"
                placeholder="Net due in"
                required
                flex
                :disabled="saving"
              />
              <LabelInputField
                input-id="payment-term-discount-pct-fixed"
                label="Discount percentage is"
                v-model="discountPercentFixed"
                type="number"
                flex
                :disabled="saving"
              />
              <LabelInputField
                input-id="payment-term-discount-within-fixed"
                label="Discount if paid within"
                v-model="discountIfPaidWithinFixed"
                placeholder="Discount if paid within"
                flex
                :disabled="saving"
              />
            </div>

            <div v-else class="payment-term-modal-fields">
              <LabelInputField
                input-id="payment-term-net-due-before"
                label="Net due before"
                v-model="netDueBefore"
                placeholder="Net due before"
                required
                flex
                :disabled="saving"
              />
              <LabelInputField
                input-id="payment-term-discount-pct-date"
                label="Discount percentage is"
                v-model="discountPercentDate"
                type="number"
                flex
                :disabled="saving"
              />
              <LabelInputField
                input-id="payment-term-discount-before-date"
                label="Discount if paid before"
                v-model="discountIfPaidBeforeDate"
                placeholder="Discount if paid before"
                flex
                :disabled="saving"
              />
            </div>

            <div v-if="error" class="payment-term-modal-error">{{ error }}</div>
          </div>

          <div class="payment-term-modal-actions payment-term-modal-actions--glass">
            <SecondaryButton label="Cancel" :disabled="saving" @click="$emit('cancel')" />
            <PrimaryButton label="Save" :loading="saving" @click="submitTerm" />
          </div>
        </div>
      </Transition>
    </div>
  </Transition>

  <ErrorModal
    :show="showErrorModal"
    title="Validation Error"
    :message="errorMessage"
    buttonText="OK"
    @close="closeErrorModal"
  />
  </Teleport>
</template>

<script>
import '../styles/AddNewPaymentTerm.css';
import ApiService from '@/services/api.service';
import ErrorModal from '@/components/ErrorModal.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'AddNewPaymentTerm',
  components: {
    ErrorModal,
    LabelInputField,
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: [String, Number],
      required: true,
    },
    existingTerms: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      termName: '',
      termType: 'fixed',
      netDueIn: '',
      discountPercentFixed: 0,
      discountIfPaidWithinFixed: '',
      netDueBefore: '',
      discountPercentDate: 0,
      discountIfPaidBeforeDate: '',
      error: '',
      saving: false,
      showErrorModal: false,
      errorMessage: '',
    };
  },
  methods: {
    async submitTerm() {
      this.error = '';
      if (!this.termName) {
        this.errorMessage = 'Payment term name is required.';
        this.showErrorModal = true;
        return;
      }

      if (this.termType === 'fixed') {
        if (!this.netDueIn || this.netDueIn.trim() === '') {
          this.errorMessage = 'Net due in (days) is required for Fixed Number of Days payment terms.';
          this.showErrorModal = true;
          return;
        }
        if (isNaN(parseInt(this.netDueIn)) || parseInt(this.netDueIn) <= 0) {
          this.errorMessage = 'Net due in (days) must be a valid positive number.';
          this.showErrorModal = true;
          return;
        }

        const dueDaysValue = parseInt(this.netDueIn);
        const duplicateTerm = this.existingTerms.find((term) => {
          const termDays = term.dueDays || term.netDueIn || term.netDueBefore || 0;
          return parseInt(termDays) === dueDaysValue;
        });

        if (duplicateTerm) {
          this.errorMessage = `A payment term with ${dueDaysValue} days already exists (${duplicateTerm.name}). Please use a different number of days.`;
          this.showErrorModal = true;
          return;
        }
      }

      if (this.termType === 'date') {
        if (!this.netDueBefore || this.netDueBefore.trim() === '') {
          this.errorMessage = 'Net due before date is required for Date Driven payment terms.';
          this.showErrorModal = true;
          return;
        }
      }

      const payload = {
        name: this.termName,
        type: this.termType,
        description: this.termType === 'fixed' ? `Pay with in ${this.netDueIn} days` : 'Date Driven',
        dueDays: this.termType === 'fixed' ? parseInt(this.netDueIn) : null,
        netDueIn: this.termType === 'fixed' ? parseInt(this.netDueIn) : null,
        discountPercent: this.termType === 'fixed' ? this.discountPercentFixed : this.discountPercentDate,
        discountIfPaidWithin:
          this.termType === 'fixed'
            ? this.discountIfPaidWithinFixed
              ? parseInt(this.discountIfPaidWithinFixed)
              : null
            : null,
        netDueBefore: this.termType === 'date' ? this.netDueBefore : null,
        discountIfPaidBefore: this.termType === 'date' ? this.discountIfPaidBeforeDate : null,
        companyId: parseInt(this.companyId),
      };

      this.saving = true;
      try {
        const response = await ApiService.createPaymentTerm(this.companyId, payload);
        const term = response && response.data ? response.data : response;
        this.$emit('ok', term);
        this.$emit('cancel');
        this.resetForm();
      } catch (err) {
        if (err.response && err.response.status === 409) {
          this.errorMessage =
            (err.response.data && err.response.data.error) ||
            'A payment term with this number of days already exists. Please use a different number of days.';
        } else if (err.response && err.response.status === 500) {
          this.errorMessage = 'An unexpected server error occurred. Please try again later.';
        } else {
          this.errorMessage =
            (err.response && err.response.data && err.response.data.error) ||
            err.message ||
            'Failed to add payment term.';
        }
        this.showErrorModal = true;
      } finally {
        this.saving = false;
      }
    },
    resetForm() {
      this.termName = '';
      this.termType = 'fixed';
      this.netDueIn = '';
      this.discountPercentFixed = 0;
      this.discountIfPaidWithinFixed = '';
      this.netDueBefore = '';
      this.discountPercentDate = 0;
      this.discountIfPaidBeforeDate = '';
      this.error = '';
      this.showErrorModal = false;
      this.errorMessage = '';
      this.saving = false;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
  watch: {
    visible(val) {
      if (!val) this.resetForm();
    },
  },
};
</script>
